import Seo from "@/components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageMasthead from "@/components/PageMasthead";
import { FadeUp, Stagger, StaggerItem } from "@/components/motion";
import { VALUES, HISTORY, GROUP_STATS } from "@/content/site";

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About"
      path="/about"
      description="Which Media is one group behind multiple specialist education brands — trusted by families, schools, investors and governments across seven international markets."
    />
    <PageMasthead
      kicker="About Which Media"
      titleLines={[{ text: "We make" }, { text: "education", accent: true }, { text: "better" }]}
      standfirst="Which Media is the group behind the region's most trusted education publications, awards, data platforms and consultancy. For over a decade we have brought transparency and rigour to a market that families navigate with everything at stake — and that investors commit tens of millions to build."
    />

    {/* Intro narrative */}
    <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <FadeUp>
          <p className="kicker mb-4">Who we are</p>
          <h2 className="headline text-3xl md:text-4xl uppercase text-ink">
            One group. Multiple brands. A single standard of trust.
          </h2>
        </FadeUp>
      </div>
      <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-lg leading-relaxed text-foreground/80">
        <FadeUp>
          <p>
            Which Media began in 2013 with a simple conviction: that parents deserved the truth about
            schools, told without fear or favour. What started as WhichSchoolAdvisor.com — the Gulf's
            first genuinely independent, parent-first school resource — has grown into a portfolio of
            brands that together shape how the region understands education.
          </p>
        </FadeUp>
        <FadeUp delay={0.05}>
          <p>
            Today our reviews guide parents across seven international markets and three continents. Our
            comparison tools help families draw their final shortlist. Our awards — the Top Schools
            Awards, described by UAE media as the "Oscars of education" — celebrate the very best. And
            our data and consultancy arms, EdinformaPlus and the Education Intelligence Group, turn a
            decade of proprietary insight into decisions for owners, investors and banks.
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="text-ink font-semibold">
            Independent, evidence-led and relentlessly parent-first — that is what has made us the most
            trusted name in education intelligence, and is the same guiding philosophy that will define
            the success of the up and coming WhichUniversityAdvisor.com.
          </p>
        </FadeUp>
      </div>
    </section>

    {/* Stats */}
    <section className="bg-ink text-white">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
        <Stagger className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {GROUP_STATS.map((s) => (
            <StaggerItem key={s.label} className="border-t-2 border-white/20 pt-5">
              <p className="headline-xl text-5xl md:text-7xl text-primary">{s.value}</p>
              <p className="mt-3 text-base font-bold">{s.label}</p>
              <p className="mt-1 text-sm text-white/55">{s.sub}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* Values */}
    <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
      <FadeUp>
        <p className="kicker mb-4">What we stand for</p>
        <h2 className="headline text-4xl md:text-6xl uppercase text-ink max-w-2xl">Four principles</h2>
      </FadeUp>
      <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14">
        {VALUES.map((v) => (
          <StaggerItem key={v.n} className="flex gap-6">
            <span className="headline-xl text-5xl md:text-6xl text-primary/25 shrink-0">{v.n}</span>
            <div>
              <h3 className="headline text-2xl text-ink mb-3">{v.title}</h3>
              <p className="text-[16px] leading-relaxed text-foreground/75">{v.body}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>

    {/* Timeline */}
    <section className="bg-secondary">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
        <FadeUp>
          <p className="kicker mb-4">Our history</p>
          <h2 className="headline text-4xl md:text-6xl uppercase text-ink max-w-3xl">
            A decade in the making
          </h2>
        </FadeUp>
        <div className="mt-14 space-y-0">
          {HISTORY.map((h, i) => (
            <FadeUp key={h.year} delay={i * 0.04}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8 border-t border-foreground/15">
                <p className="md:col-span-3 headline text-3xl md:text-4xl text-primary">{h.year}</p>
                <div className="md:col-span-9">
                  <h3 className="headline text-xl md:text-2xl text-ink mb-2">{h.title}</h3>
                  <p className="text-[16px] leading-relaxed text-foreground/75 max-w-3xl">{h.body}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
      <div className="bg-primary text-primary-foreground rounded-3xl p-10 md:p-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <h2 className="headline text-3xl md:text-5xl uppercase max-w-2xl">
          Partner with the most trusted name in education
        </h2>
        <Link to="/contact" className="shrink-0 inline-flex items-center gap-2 font-sans text-base font-bold bg-white text-ink rounded-full pl-8 pr-6 py-4 hover:bg-ink hover:text-white transition-colors">
          Get in touch <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  </div>
);

export default About;
